/**
 * OpenRouter AI Service
 * 
 * Provides AI-powered content generation using OpenRouter API
 * with Llama vision models for analyzing images and generating content.
 */

const OPENROUTER_API_KEY = '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';
const OPENROUTER_BASE_URL = 'https://openrouter.ai/api/v1';

// Free vision models available on OpenRouter (fallback order)
const VISION_MODELS = [
    'qwen/qwen2.5-vl-32b-instruct:free',      // Qwen - Very capable
    'google/gemma-3-27b-it:free',              // Gemma 3 - Good vision
    'mistralai/mistral-small-3.1-24b-instruct:free', // Mistral Small
    'google/gemini-2.0-flash-exp:free',        // Gemini Flash
];

// Free text models
const TEXT_MODELS = [
    'google/gemma-3-12b-it:free',
    'mistralai/mistral-small-3.1-24b-instruct:free',
    'google/gemini-2.0-flash-exp:free',
];

interface OpenRouterMessage {
    role: 'system' | 'user' | 'assistant';
    content: string | Array<{ type: 'text' | 'image_url'; text?: string; image_url?: { url: string } }>;
}

interface OpenRouterResponse {
    choices: Array<{
        message: {
            content: string;
        };
    }>;
}

/**
 * Send a chat completion request to OpenRouter with automatic fallback
 */
async function chatCompletion(
    messages: OpenRouterMessage[],
    useVision: boolean = false
): Promise<string> {
    const models = useVision ? VISION_MODELS : TEXT_MODELS;

    for (const model of models) {
        try {
            console.log(`[OpenRouter] Trying model: ${model}`);

            const response = await fetch(`${OPENROUTER_BASE_URL}/chat/completions`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
                    'Content-Type': 'application/json',
                    'HTTP-Referer': window.location.origin,
                    'X-Title': 'Cassio Lemos Portfolio Admin',
                },
                body: JSON.stringify({
                    model,
                    messages,
                    max_tokens: 1500,
                    temperature: 0.7,
                }),
            });

            if (response.ok) {
                const data: OpenRouterResponse = await response.json();
                const content = data.choices[0]?.message?.content;
                if (content) {
                    console.log(`[OpenRouter] Success with model: ${model}`);
                    return content;
                }
            }

            // If 429 (rate limit) or 503 (unavailable), try next model
            if (response.status === 429 || response.status === 503) {
                console.log(`[OpenRouter] Model ${model} rate limited, trying next...`);
                continue;
            }

            // For other errors, log and try next
            const error = await response.text();
            console.error(`[OpenRouter] Model ${model} error:`, error);

        } catch (err) {
            console.error(`[OpenRouter] Model ${model} failed:`, err);
        }
    }

    throw new Error('Todos os modelos de IA estão indisponíveis no momento. Tente novamente em alguns segundos.');
}

/**
 * Convert image URL to base64 for vision models
 */
async function imageToBase64(imageUrl: string): Promise<string> {
    // If already base64, return as is
    if (imageUrl.startsWith('data:')) {
        return imageUrl;
    }

    try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        console.error('Failed to convert image to base64:', error);
        throw error;
    }
}

/**
 * Parse JSON from AI response, handling markdown code blocks
 */
function parseAIResponse<T>(response: string): T {
    // Remove markdown code blocks if present
    let cleaned = response.trim();
    if (cleaned.startsWith('```json')) {
        cleaned = cleaned.slice(7);
    } else if (cleaned.startsWith('```')) {
        cleaned = cleaned.slice(3);
    }
    if (cleaned.endsWith('```')) {
        cleaned = cleaned.slice(0, -3);
    }
    cleaned = cleaned.trim();

    return JSON.parse(cleaned);
}

// ============================================
// PROJECT GENERATION
// ============================================

export interface GeneratedProjectFields {
    title: string;
    subtitle: string;
    description: string;
    disciplines: string[];
    tools: string[];
    roles: string[];
}

/**
 * Generate project fields from cover image
 */
export async function generateProjectFields(
    imageUrl: string,
    existingTitle?: string
): Promise<GeneratedProjectFields> {
    const base64Image = await imageToBase64(imageUrl);

    const systemPrompt = `Você é um assistente especializado em portfólio de design e desenvolvimento web.
Analise a imagem do projeto e gere informações relevantes em português brasileiro.
Seja criativo mas profissional. Foque em qualidade e impacto.
Responda APENAS em JSON válido, sem markdown ou explicações.`;

    const userPrompt = `Analise esta imagem de um projeto de design/desenvolvimento e gere:
${existingTitle ? `O projeto se chama "${existingTitle}".` : ''}

Retorne um JSON com:
{
  "title": "Título criativo e impactante (max 50 chars)",
  "subtitle": "Categoria ou tipo do projeto (ex: 'E-commerce Redesign', 'Identidade Visual')",
  "description": "Descrição detalhada do projeto em 2-3 frases, mencionando objetivo, solução e impacto",
  "disciplines": ["lista de 3-5 disciplinas relevantes como 'UI Design', 'Branding', 'Frontend', etc"],
  "tools": ["lista de 3-6 ferramentas/tecnologias prováveis como 'Figma', 'React', 'Photoshop', etc"],
  "roles": ["lista de 2-4 funções desempenhadas como 'Lead Designer', 'Developer', etc"]
}`;

    const response = await chatCompletion([
        { role: 'system', content: systemPrompt },
        {
            role: 'user',
            content: [
                { type: 'text', text: userPrompt },
                { type: 'image_url', image_url: { url: base64Image } }
            ]
        }
    ], true);

    return parseAIResponse<GeneratedProjectFields>(response);
}

// ============================================
// SERVICE GENERATION
// ============================================

export interface GeneratedServiceFields {
    title: string;
    short_description: string;
    full_description: string;
    features: string[];
    benefits: string[];
}

/**
 * Generate service description fields
 */
export async function generateServiceFields(
    serviceName: string,
    category?: string
): Promise<GeneratedServiceFields> {
    const systemPrompt = `Você é um copywriter especializado em serviços de design e desenvolvimento.
Crie textos persuasivos e profissionais em português brasileiro.
Responda APENAS em JSON válido, sem markdown ou explicações.`;

    const userPrompt = `Gere conteúdo para o serviço "${serviceName}"${category ? ` na categoria ${category}` : ''}.

Retorne um JSON com:
{
  "title": "Título do serviço (pode ser o mesmo ou uma variação)",
  "short_description": "Descrição curta de 1 linha para cards",
  "full_description": "Descrição completa em 2-3 parágrafos explicando o serviço",
  "features": ["lista de 4-6 características do serviço"],
  "benefits": ["lista de 3-5 benefícios para o cliente"]
}`;

    const response = await chatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
    ], false);

    return parseAIResponse<GeneratedServiceFields>(response);
}

// ============================================
// TESTIMONIAL GENERATION
// ============================================

export interface GeneratedTestimonialFields {
    improved_text: string;
    suggested_highlights: string[];
}

/**
 * Improve testimonial text
 */
export async function improveTestimonial(
    originalText: string,
    clientName?: string
): Promise<GeneratedTestimonialFields> {
    const systemPrompt = `Você é um editor especializado em depoimentos de clientes.
Melhore o texto mantendo a autenticidade e tom pessoal.
Responda APENAS em JSON válido, sem markdown ou explicações.`;

    const userPrompt = `Melhore este depoimento${clientName ? ` de ${clientName}` : ''}:
"${originalText}"

Mantenha a essência e autenticidade, mas melhore clareza e impacto.

Retorne um JSON com:
{
  "improved_text": "Versão melhorada do depoimento",
  "suggested_highlights": ["frases de destaque que podem ser usadas como quotes"]
}`;

    const response = await chatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
    ], false);

    return parseAIResponse<GeneratedTestimonialFields>(response);
}

// ============================================
// ABOUT/CONTENT GENERATION
// ============================================

export interface GeneratedAboutContent {
    headline: string;
    bio_short: string;
    bio_full: string;
    skills_description: string;
    cta_text: string;
}

/**
 * Generate About page content
 */
export async function generateAboutContent(
    name: string,
    role: string,
    yearsExperience?: number,
    specialties?: string[]
): Promise<GeneratedAboutContent> {
    const systemPrompt = `Você é um copywriter especializado em páginas pessoais de criativos.
Crie textos autênticos, profissionais e impactantes em português brasileiro.
Responda APENAS em JSON válido, sem markdown ou explicações.`;

    const userPrompt = `Gere conteúdo para a página About de ${name}, ${role}.
${yearsExperience ? `Experiência: ${yearsExperience} anos.` : ''}
${specialties?.length ? `Especialidades: ${specialties.join(', ')}.` : ''}

Retorne um JSON com:
{
  "headline": "Frase de impacto curta para o header",
  "bio_short": "Bio resumida de 1-2 linhas para uso em cards",
  "bio_full": "Bio completa em 2-3 parágrafos contando a jornada profissional",
  "skills_description": "Parágrafo descrevendo habilidades e diferenciais",
  "cta_text": "Texto persuasivo para call-to-action convidando a entrar em contato"
}`;

    const response = await chatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt }
    ], false);

    return parseAIResponse<GeneratedAboutContent>(response);
}

// ============================================
// GENERIC TEXT GENERATION
// ============================================

/**
 * Generate any text based on context
 */
export async function generateText(
    prompt: string,
    context?: string
): Promise<string> {
    const systemPrompt = `Você é um assistente de copywriting para um portfólio de design/desenvolvimento.
Escreva em português brasileiro, tom profissional mas acessível.
${context ? `Contexto adicional: ${context}` : ''}`;

    const response = await chatCompletion([
        { role: 'system', content: systemPrompt },
        { role: 'user', content: prompt }
    ], false);

    return response;
}

// ============================================
// IMAGE ANALYSIS
// ============================================

/**
 * Analyze an image and return a description
 */
export async function analyzeImage(imageUrl: string): Promise<string> {
    const base64Image = await imageToBase64(imageUrl);

    const response = await chatCompletion([
        {
            role: 'system',
            content: 'Você é um especialista em análise visual. Descreva imagens de projetos de design de forma detalhada e profissional em português brasileiro.'
        },
        {
            role: 'user',
            content: [
                { type: 'text', text: 'Descreva detalhadamente esta imagem de projeto, incluindo cores, estilo, elementos visuais e possível propósito.' },
                { type: 'image_url', image_url: { url: base64Image } }
            ]
        }
    ], true);

    return response;
}
